import { Link, useNavigate } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useSidebar } from "@/components/ui/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Bell, Search, Settings, LogOut, User, LayoutDashboard, Menu } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useAdminAuth } from "@/context/Admin"

export default function AdminDashboardHeader() {
  const { toggleSidebar } = useSidebar()
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  const adminName = admin?.name || "Admin User";
  const adminRole = admin?.role || "admin";
  const avatarInitials = adminName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <header className="sticky top-0 z-50 flex h-16 shrink-0 items-center gap-4 border-b bg-white px-4 md:px-6">
      {/* Mobile Sidebar Trigger */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden text-primary"
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Desktop Logo/Dashboard Title */}
      <Link to="/admin" className="hidden md:flex items-center gap-2 text-lg font-semibold text-primary">
        <LayoutDashboard className="w-5 h-5 text-secondary" />
        <span>Admin Dashboard</span>
      </Link>

      {/* Search Bar */}
      <div className="flex-1 relative max-w-md ml-auto hidden md:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="search"
          placeholder="Search admin data..."
          className="w-full rounded-lg bg-gray-100 pl-9 focus:border-secondary focus:ring-secondary"
        />
      </div>

      {/* Right-aligned actions */}
      <div className="flex items-center gap-4 ml-auto md:ml-0">
        <Button variant="ghost" size="icon" className="text-primary hover:text-secondary">
          <Bell className="w-5 h-5" />
          <span className="sr-only">Notifications</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage 
                  src={admin?.avatar_url || "/placeholder.svg"} 
                  alt="Admin Avatar"
                  onError={(e) => {
                    e.target.src = "/placeholder.svg";
                  }}
                />
                <AvatarFallback className="bg-secondary text-primary">{avatarInitials}</AvatarFallback>
              </Avatar>
              <span className="sr-only">Admin menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{adminName}</p>
                <p className="text-xs leading-none text-muted-foreground">{admin?.email}</p>
                <p className="text-xs leading-none text-muted-foreground capitalize">
                  {adminRole === "super_admin" ? "Super Administrator" : "Administrator"}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/admin/profile" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/admin/settings" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {/* Logout with AlertDialog Trigger outside DropdownMenuItem */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()} // prevent DropdownMenu from closing
                >
                  <LogOut className="h-4 w-4" />
                  <span>Log out</span>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will be logged out and redirected to the login page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLogout}>
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
